import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { SERVICES, SITE } from "@/lib/site";

const TIME_SLOTS = [
  "10:30 AM",
  "11:00 AM",
  "11:30 AM",
  "12:00 PM",
  "12:30 PM",
  "01:00 PM",
  "02:00 PM",
  "02:30 PM",
  "06:00 PM",
  "06:30 PM",
  "07:00 PM",
  "07:30 PM",
  "08:00 PM",
  "08:30 PM",
];


export default function AppointmentForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    location: "Hosa Road",
    notes: "",
  });
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service || !date || !time) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        service: form.service,
        location: form.location,
        preferred_date: format(date, "dd/MM/yyyy"),
        preferred_time: time,
        notes: form.notes.trim() || null,
      };
      setSuccess(true);
      toast.success("Opening WhatsApp...");

      // Build WhatsApp message
      const msg = encodeURIComponent(
        `Hi Vinodha Diagnostics, I'd like to book an appointment.\n\nName: ${payload.name}\nPhone: ${payload.phone}\nService: ${payload.service}\nBranch: ${payload.location}\nDate: ${payload.preferred_date}\nTime: ${payload.preferred_time}`
      );
      window.open(`https://wa.me/${SITE.whatsappRaw}?text=${msg}`, "_blank");
    } catch (err) {
      console.error(err);
      toast.error("Could not submit. Please try WhatsApp or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section id="appointment" className="bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <div
            data-testid="appointment-success"
            className="bg-white border border-gray-200 rounded-2xl p-10 text-center shadow-sm"
          >
            <div className="mx-auto w-14 h-14 grid place-items-center rounded-full bg-[#ECFDF5] text-[#10B981]">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h3 className="font-display mt-5 text-2xl font-bold text-[#0B2545]">
              Your request is in!
            </h3>
            <p className="mt-2 text-[#64748B]">
              Our team will call you within 30 minutes to confirm your slot. A WhatsApp
              message has also been prepared for quick confirmation.
            </p>
            <Button
              onClick={() => {
                setSuccess(false);
                setForm({
                  name: "",
                  phone: "",
                  email: "",
                  service: "",
                  location: "Hosa Road",
                  notes: "",
                });
                setDate(undefined);
                setTime("");
              }}
              data-testid="book-another-btn"
              className="mt-6 rounded-full bg-[#0369A1] hover:bg-[#0284C7] text-white px-7 h-11"
            >
              Book another
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="appointment" data-testid="appointment-section" className="bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12 py-16 md:py-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#0369A1]">
            Online Booking
          </span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B2545]">
            Book your test in under a minute.
          </h2>
          <p className="mt-4 text-[#64748B] leading-relaxed">
            Choose your preferred branch, date and time. We&apos;ll confirm via call &amp;
            WhatsApp within 30 minutes.
          </p>

          <div className="mt-8 bg-white rounded-2xl border border-gray-200 p-5">
            <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#0369A1]">
              Need help?
            </div>
            <a
              href={`tel:${SITE.phoneRaw}`}
              className="block font-display mt-1 text-2xl font-bold text-[#0B2545] hover:text-[#0369A1]"
            >
              {SITE.phone}
            </a>
            <div className="text-sm text-[#64748B] mt-1">Mon–Sat · 8:00 AM – 9:00 PM</div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="appointment-form"
          className="lg:col-span-7 bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-sm space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full Name *">
              <Input
                data-testid="appt-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Your full name"
                required
                className="h-11"
              />
            </Field>
            <Field label="Phone Number *">
              <Input
                data-testid="appt-phone"
                value={form.phone}
                onChange={update("phone")}
                type="tel"
                placeholder="+91 ..."
                required
                className="h-11"
              />
            </Field>
          </div>

          <Field label="Email (optional)">
            <Input
              data-testid="appt-email"
              value={form.email}
              onChange={update("email")}
              type="email"
              placeholder="you@example.com"
              className="h-11"
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Service *">
              <Select value={form.service} onValueChange={update("service")}>
                <SelectTrigger data-testid="appt-service" className="h-11">
                  <SelectValue placeholder="Choose service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICES.map((s) => (
                    <SelectItem key={s.slug} value={s.name}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Branch *">
              <Select value={form.location} onValueChange={update("location")}>
                <SelectTrigger data-testid="appt-location" className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Hosa Road">Hosa Road (Main Office)</SelectItem>
                  <SelectItem value="Hebbagodi">Hebbagodi Branch</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Preferred Date * (DD/MM/YYYY)">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="appt-date"
                    className={cn(
                      "h-11 w-full justify-start text-left font-normal border-input",
                      !date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "dd/MM/yyyy") : "Select a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>

            <Field label="Preferred Time * (AM/PM)">
              <Select value={time} onValueChange={setTime}>
                <SelectTrigger data-testid="appt-time" className="h-11">
                  <SelectValue placeholder="Choose time slot" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Notes (optional)">
            <Textarea
              data-testid="appt-notes"
              value={form.notes}
              onChange={update("notes")}
              placeholder="Any prescriptions, allergies, or referring doctor details"
              rows={3}
            />
          </Field>

          <Button
            type="submit"
            data-testid="appt-submit"
            disabled={loading}
            className="w-full sm:w-auto rounded-full bg-[#0369A1] hover:bg-[#0284C7] text-white h-12 px-8 font-semibold"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…
              </>
            ) : (
              "Request Appointment"
            )}
          </Button>

          <p className="text-xs text-[#64748B]">
            By submitting, you agree to be contacted via call/WhatsApp on the number
            provided. We never share your information.
          </p>
        </form>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold tracking-[0.12em] uppercase text-[#0B2545]/80">
        {label}
      </Label>
      {children}
    </div>
  );
}
